// Tone API Client for E007-S018
import { apiClient } from './apiClient';
import { EmotionalTone } from '../components/EmotionalToneFilter';

export interface ToneClassification {
  creative_id: string;
  primary_tone: EmotionalTone;
  primary_confidence: number;
  secondary_tone: EmotionalTone | null;
  secondary_confidence: number;
  key_phrases: string[];
  reasoning: string;
  classified_at: string;
}

export interface BatchClassifyJob {
  job_id: string;
  total_creatives: number;
  estimated_duration: string;
  status: 'queued' | 'processing' | 'complete' | 'failed';
}

export interface BatchJobStatus {
  job_id: string;
  status: 'queued' | 'processing' | 'complete' | 'failed';
  progress: {
    total: number;
    completed: number;
    failed: number;
    percent: number;
  };
  error_message?: string;
}

export interface ToneDistribution {
  tone: EmotionalTone;
  count: number;
  percent: number;
  avg_performance: number;
  avg_ctr: number;
  avg_roas: number;
}

export interface ToneAnalytics {
  distribution: ToneDistribution[];
  total_creatives: number;
  insights: string[];
}

export interface ToneMetadata {
  name: EmotionalTone;
  emoji: string;
  color: string;
  description: string;
}

export interface ToneOverrideRequest {
  new_tone: EmotionalTone;
  reason?: string;
  use_for_training?: boolean;
}

export const toneApi = {
  /**
   * Classify a single creative's emotional tone
   */
  async classifyCreative(creativeId: string): Promise<ToneClassification> {
    const response = await apiClient.post(`/tone/creatives/${creativeId}/classify`);
    return response.data;
  },

  /**
   * Start a batch classification job for multiple creatives
   * @param creativeIds Optional list of IDs; null = all unclassified
   */
  async batchClassify(creativeIds?: string[]): Promise<BatchClassifyJob> {
    const response = await apiClient.post('/tone/batch-classify', {
      creative_ids: creativeIds || null
    });
    return response.data;
  },

  /**
   * Get the status of a batch classification job
   */
  async getBatchStatus(jobId: string): Promise<BatchJobStatus> {
    const response = await apiClient.get(`/tone/batch-classify/${jobId}`);
    return response.data;
  },

  /**
   * Get tone distribution and analytics
   */
  async getDistribution(dateRange?: { from: string; to: string }): Promise<ToneAnalytics> {
    const params = dateRange ? { date_from: dateRange.from, date_to: dateRange.to } : {};
    const response = await apiClient.get('/tone/distribution', { params });
    return response.data;
  },

  /**
   * Manually override a creative's tone classification
   */
  async overrideTone(
    creativeId: string,
    newTone: EmotionalTone,
    reason?: string,
    useForTraining: boolean = true
  ): Promise<ToneClassification> {
    const response = await apiClient.put(`/tone/creatives/${creativeId}/override`, {
      new_tone: newTone,
      reason,
      use_for_training: useForTraining
    });
    return response.data;
  },

  /**
   * Get all available tones with metadata
   */
  async getTones(): Promise<ToneMetadata[]> {
    const response = await apiClient.get('/tone/tones');
    return response.data;
  },

  /**
   * Poll batch job until complete
   */
  async pollBatchJob(
    jobId: string,
    onProgress: (status: BatchJobStatus) => void,
    intervalMs: number = 2000
  ): Promise<BatchJobStatus> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getBatchStatus(jobId);
          onProgress(status);

          if (status.status === 'complete') {
            resolve(status);
          } else if (status.status === 'failed') {
            reject(new Error(status.error_message || 'Batch classification failed'));
          } else {
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };
      poll();
    });
  }
};

export default toneApi;
